
import Vue from 'vue';
import {Component, Prop} from "vue-property-decorator";
import {getModule} from "vuex-module-decorators";
import {CustomerModule, ShopModule, store} from "@returns-centre/store";
import {VueModal} from "@/components";
import {AppButtonSelection, TradeInIneligibleProductModal} from "@returns-centre/components";
import { getItemImage } from '@returns-centre/utils';
import questionMarkIcon from '@returns-centre/resources/questionMarkIcon.svg';
import {find, findIndex, isEmpty} from 'lodash';

@Component({
    components: {
        AppButtonSelection,
        VueModal,
        TradeInIneligibleProductModal
    }
})
export default class TradeInOrderLineQuestionsModal extends Vue {
    @Prop({default: null}) lineItem!: OrderLineItem;
    @Prop({default: null}) selectedTradeInProductRecord!: TradeInProductRecord;

    isUpdating: boolean = false;
    hasValidationError: boolean = false;
    questionMarkIcon = questionMarkIcon;
    selectedConditionAnswerOption: ProductQuestionOption[] = [];
    hasTradeInIneligibleProduct: boolean = false;
    tradeInIneligibleProductTitle: string = '';
    tradeInIneligibleProductContent: string = '';
    customerModule = getModule(CustomerModule, store);
    shopModule = getModule(ShopModule, store);

    get questions(): RegisteredProductQuestion[] {
        return this.shopModule.shop?.registered_product_questions?.filter(
            (item) => item.use_for_order_history && item.use_for_trade_in) || [];
    }

    mounted() {
        this.setConditionQuestionAnswerOptionStyle();

        if (!this.questions.length) {
            this.confirm();
        }
    }

    resolveQuestionAttributeSelectedValue(question: RegisteredProductQuestion) {
        if(!this.isUpdating) { return  null;}

        const result = find(this.lineItem.product_answers, ['question', question.question_name]);
        if(!result) { return  null;}

        if(question.question_type!.slug == 'condition') {
            setTimeout(() => this.setConditionQuestionAnswerOptionStyle(question, result.answer), 500);
            return {};
        }
        return result;
    }

    doesQuestionsAnswerRequiresAdditionalInput(
        question: RegisteredProductQuestion,
        staticAnswerOptionValue = 'Someone else'
    ) {

        return (findIndex(this.lineItem.product_answers, {
            question: question.question_name,
            answer: staticAnswerOptionValue
        }) >= 0);
    }

    setConditionQuestionAnswerOptionStyle(question: RegisteredProductQuestion|null = null, answer: string|null = null) {
        for (const key of Object.keys(this.$refs)) {
            if(key.includes('conditionQuestionAnswerOption_' + question?.id)) {
                // @ts-ignore
                (this.$refs[key][0]! as HTMLDivElement).style.backgroundColor = '#ffffff';
                // @ts-ignore
                (this.$refs[key][0]! as HTMLDivElement).style.color = '#000000';
                // @ts-ignore
                (this.$refs[key][0]! as HTMLDivElement).style.border = '1px solid #000000';
            }
        }

        if(answer) {
            const refName = 'conditionQuestionAnswerOption_'+ question?.id +'_' + answer;
            // @ts-ignore
            (this.$refs[refName][0]! as HTMLDivElement).style.backgroundColor = '#000000';
            // @ts-ignore
            (this.$refs[refName][0]! as HTMLDivElement).style.color = '#ffffff';
            // @ts-ignore
            (this.$refs[refName][0]! as HTMLDivElement).style.border = '#000000';
        }
    }

    onOptionSelected(question: RegisteredProductQuestion, answer: string, answerDescription: string = '') {
       this.resolveTradeInLineItemQuestions(question, answer, answerDescription);
    }

    onTextInputChanged(question: RegisteredProductQuestion, answer: string) {
        this.resolveTradeInLineItemQuestions(question, answer);
    }

    onOverrideAnswerOptionTextInputChanged(question: RegisteredProductQuestion, answer: string) {
        if(isEmpty(answer)) { return;}
        this.resolveTradeInLineItemQuestions(question, '', '', answer);
    }

    onConditionQuestionAnswerOptionSelected(
        question: RegisteredProductQuestion,
        option: ProductQuestionOption
    ) {
        this.selectedConditionAnswerOption.push(option);
        this.resolveTradeInLineItemQuestions(question, option.name, option.description || '');
        this.setConditionQuestionAnswerOptionStyle(question, option.name);
    }

    resolveTradeInLineItemQuestions(
        question: RegisteredProductQuestion,
        answer: string,
        answerDescription: string = '',
        overrideAnswer: string|null = null
    ) {
        if (!this.lineItem.product_answers) {
            this.lineItem.product_answers = [];
        }

        const index = findIndex(this.lineItem.product_answers, ['question', question.question_name]);

        if(index < 0) {
            this.lineItem.product_answers?.push({
                product_question_id: question.id as number,
                question: question.question_name,
                question_reference: question.question_text!,
                question_type: question.question_type!.slug,
                answer: answer,
                answer_description: answerDescription,
                answer_supplement: overrideAnswer,
                sort_order: question.sort_order!,
            });
        } else {
            this.lineItem.product_answers![index].product_question_id = question.id as number;
            this.lineItem.product_answers![index].question = question.question_name;
            this.lineItem.product_answers![index].question_reference = question.question_text!;
            this.lineItem.product_answers![index].question_type = question.question_type!.slug;
            if(!overrideAnswer) {
                this.lineItem.product_answers![index].answer = answer
                this.lineItem.product_answers![index].answer_supplement = null;
            } else {
                this.lineItem.product_answers![index].answer_supplement = overrideAnswer;
            }
            this.lineItem.product_answers![index].answer_description = answerDescription;
        }
    }

    async confirm() {
        const answeredQuestions = this.lineItem.product_answers!.length;
        this.hasValidationError = false;

        const question = this.selectedConditionAnswerOption.find(item => item.ineligible);
        if (question) {
            this.tradeInIneligibleProductTitle = question.ineligible_title!;
            this.tradeInIneligibleProductContent = question.ineligible_description!;
            this.hasTradeInIneligibleProduct = true;
            return;
        }

        if(answeredQuestions < this.questions.length) {
            this.hasValidationError = true;
            return;
        }

        this.hasValidationError = false;
        for(const question of this.lineItem.product_answers!) {
            if(isEmpty(question.answer)) {
                this.hasValidationError = true;
                break;
            }
        }

        if(this.hasValidationError) {
            return;
        }

        this.$emit('confirmed', this.lineItem);
    }

    getProductImage(product: ProductOption): string | null {
        return getItemImage(
            product,
            this.shopModule.shop?.product_image as string,
            this.shopModule.shop?.show_product_images);
    }
}

